(ns client-portal.client.user-management.organizations.views
  (:require [client-portal.client.layout :as l]
            [client-portal.client.user-management.components :as um.c]
            [client-portal.client.user-management.data :as um.d]
            [client-portal.client.user-management.organizations.data :as organizations.d]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn- swatch [color]
  [:span {:aria-hidden true
          :style {:background-color (if (re-matches organizations.d/hex-color-pattern (or (some-> color (s/trim)) ""))
                                      (s/trim color)
                                      :transparent)
                  :border (str "1px solid " (style/alc-color "Border/divider"))
                  :border-radius 4
                  :flex "0 0 20px"
                  :height 20
                  :width 20}}])

(defn organizations-table []
  (let [organizations @(re/subscribe [:user-management/organizations-])
        concepts-by-id @(re/subscribe [:user-management/concepts-by-id-])
        member-counts @(re/subscribe [:user-management/member-counts-])
        compact? (not @(re/subscribe [:core/breakpoint-min?- :lg]))]
    (cond
      (= organizations :error)
      [empty-state/no-data-card {:text "Organizations couldn't be loaded. Reload the page to try again."
                                 :material-icon-type :error-outline}]

      (util/nil-or-loading? organizations)
      [empty-state/loading-card {:label "Loading organizations…"}]

      (empty? organizations)
      [empty-state/no-data-card {:text "There are no organizations here yet. Create one to start adding people."
                                 :material-icon-type :domain-add}]

      :else
      [:div {:style {:max-width um.c/max-content-width}}
       [alc/table {:no-data-text "No organizations"
                   :row-style {:align-items :center}
                   :flex-vals (if compact?
                                [1 "0 0 96px"]
                                [1.4 2 "0 0 120px" "0 0 80px"])
                   :headers (if compact?
                              ["Name" "Members"]
                              ["Name" "Concepts" "Members" "Brand"])
                   :rows (for [{:keys [tenant-organization-id concept-ids primary-color secondary-color] :as organization}
                               organizations
                               :let [members (get member-counts tenant-organization-id 0)
                                     concept-names (->> concept-ids
                                                        (keep #(get-in concepts-by-id [% :label]))
                                                        (sort))]]
                           {:cells (cond-> [{:label [um.c/organization-name organization]}]
                                           (not compact?)
                                           (conj {:label (if (empty? concept-ids)
                                                           [um.c/secondary-text {:style {:font-size 12}} "No concepts yet"]
                                                           [:span {:style {:display :flex
                                                                           :flex-direction :column}}
                                                            (um.c/count-text (count concept-ids) "concept" "concepts")
                                                            [um.c/secondary-text {:style {:font-size 12
                                                                                          :overflow :hidden
                                                                                          :text-overflow :ellipsis
                                                                                          :white-space :nowrap}}
                                                             (s/join ", " concept-names)]])})
                                           :always
                                           (conj {:label [alc/up-ghost-button {:style {:height 28
                                                                                       :margin-left -8
                                                                                       :padding "0 8px"}
                                                                               :disabled (zero? members)
                                                                               :on-click (fn [e]
                                                                                           (.stopPropagation e)
                                                                                           (re/dispatch [:user-management/show-organization-members
                                                                                                         tenant-organization-id]))}
                                                          (um.c/count-text members "person" "people")]})
                                           (not compact?)
                                           (conj {:label (if (or primary-color secondary-color)
                                                           [:span {:style {:display :flex
                                                                           :gap 4}}
                                                            [swatch primary-color]
                                                            [swatch secondary-color]]
                                                           [um.c/secondary-text {:style {:font-size 12}} "None"])}))
                            :on-click #(re/dispatch [:user-management/edit-organization organization])})}]])))

(defn section []
  [l/standard-content-container {:title "Organizations"
                                 :header-contents [um.c/toolbar
                                                   [um.c/tenant-dropdown]
                                                   [um.c/secondary-text {}
                                                    "Each organization is a group in Auth0. Its members see the concepts it covers."]
                                                   [um.c/spacer]
                                                   [alc/up-call-to-action-button {:icon-type :AddIcon
                                                                                  :on-click #(re/dispatch [:user-management/edit-organization nil])}
                                                    "New Organization"]]
                                 :sticky-header? true
                                 :container-style {:flex 1
                                                   :min-height :auto}}
   [organizations-table]])

(defn- field [{:keys [label id hint]} & contents]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :gap 6}}
   [:label {:html-for id
            :style {:color (style/alc-color "Text/primary_header")
                    :font-size 14
                    :font-weight 500}}
    label]
   (into [:<>] contents)
   (when hint
     [um.c/secondary-text {} hint])])

(defmethod alc/dialog-view
  :user-management/organization-dialog [_ _]
  (let [{:keys [tenant-organization-id external-id label org-logo primary-color] :as form}
        @(re/subscribe [:user-management/organization-form-])
        concepts (um.d/rows @(re/subscribe [:user-management/concepts-]))
        problems (organizations.d/problems form)]
    [alc/dialog {:title (if tenant-organization-id "Edit Organization" "New Organization")
                 :description (if tenant-organization-id
                                (str "Changes apply to its Auth0 organization too (" external-id ").")
                                "This also creates the organization in Auth0, with email and password sign-in turned on.")
                 :footer [:<>
                          [alc/up-call-to-action-button {:loading? @(re/subscribe [:user-management/saving-organization?-])
                                                         :disabled (not-empty problems)
                                                         :on-click #(re/dispatch [:user-management/save-organization])}
                           (if tenant-organization-id "Save Changes" "Create Organization")]
                          [alc/up-button {:on-click #(re/dispatch [:dialog/hide])}
                           "Cancel"]]}
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 18
                    :max-width "100%"
                    :width 560}}
      [field {:label "Name"
              :id "um-organization-name"}
       [alc/up-input {:id "um-organization-name"
                      :form-path organizations.d/form-path
                      :form-name :label
                      :auto-focus true
                      :placeholder "Acme Restaurants"}]]
      [field {:label "Concepts"
              :hint "Members see these concepts' data. Removing one takes it away from everyone in the organization."}
       [alc/up-multiple-dropdown {:form-path organizations.d/form-path
                                  :form-name :concept-ids
                                  :aria-label "Concepts"
                                  :placeholder (if concepts "Choose concepts" "Loading concepts…")
                                  :options (mapv (fn [{:keys [id label]}]
                                                   {:text label
                                                    :value id})
                                                 (sort-by #(s/lower-case (:label %)) concepts))}]]
      [field {:label "Logo"
              :id "um-organization-logo"
              :hint "A link to an image. It shows on their sign-in page."}
       [:div {:style {:align-items :center
                      :display :flex
                      :gap 10}}
        [um.c/organization-mark {:label label
                                 :org-logo (some-> org-logo (s/trim))
                                 :primary-color primary-color}
         {:size 36}]
        [:div {:style {:flex 1}}
         [alc/up-input {:id "um-organization-logo"
                        :form-path organizations.d/form-path
                        :form-name :org-logo
                        :placeholder "https://…/logo.svg"}]]]]
      [field {:label "Brand colors"
              :hint "Used on their sign-in page. Set both, or leave both blank."}
       [:div {:style {:display :grid
                      :gap 10
                      :grid-template-columns "repeat(auto-fit, minmax(180px, 1fr))"}}
        (for [[k placeholder] [[:primary-color "Primary, like #A01958"]
                               [:secondary-color "Background, like #4A154B"]]]
          ^{:key k}
          [:div {:style {:align-items :center
                         :display :flex
                         :gap 8}}
           [swatch (get form k)]
           [:div {:style {:flex 1}}
            [alc/up-input {:form-path organizations.d/form-path
                           :form-name k
                           :aria-label (if (= k :primary-color) "Primary color" "Background color")
                           :placeholder placeholder}]]])]
       (when-let [problem (:colors problems)]
         [um.c/error-text problem])]]]))
